import { createFileRoute, Link } from '@tanstack/react-router';
import { Group, Progress, Stack, Text } from '@mantine/core';
import { ArrowRight, Award } from 'lucide-react';
import { motion } from 'framer-motion';
import { Button, Card, EmptyState, PageContainer, PageHeader, Section, Skeleton } from '../components/ui';
import { staggerContainer, rowItem } from '../lib/motion';
import { TierBadge } from '../components/journey/TierBadge';
import { skillLabel, tierMeta } from '../components/journey/tier';
import { useMyTiers } from '../hooks/useJourney';
import type { SkillTier } from '../api/types';

export const Route = createFileRoute('/journey/result')({
  component: PlacementResultView,
});

/**
 * Placement result — the per-skill tiers awarded after grading. Reads the
 * freshly-updated tier state (the submit mutation invalidates the tiers query)
 * and shows the highest-unlocked tier + proficiency for each skill.
 */
export function PlacementResultView() {
  const { data, isPending, isError } = useMyTiers();

  const placed = (data?.tiers ?? []).filter((t) => t.source === 'placement');
  const tiers = placed.length > 0 ? placed : (data?.tiers ?? []);

  return (
    <PageContainer size="narrow">
      <PageHeader
        icon={Award}
        title="Your Results"
        description="Your assessed skill tier and recommended next steps."
        breadcrumbs={[{ label: 'Forge', to: '/' }, { label: 'Results' }]}
        actions={
          <Link to="/trainee" style={{ textDecoration: 'none' }}>
            <Button variant="primary" rightIcon={<ArrowRight size={14} />}>
              View my roadmap
            </Button>
          </Link>
        }
      />

      <Section
        title="Skill tiers"
        description="Here is where you landed per skill. Completing a tier's required work unlocks the next."
      >
        {isPending ? (
          <Skeleton lines={4} height={28} gap={12} />
        ) : isError ? (
          <Card variant="danger">
            <Text c="red" size="sm">
              Failed to load your placement result.
            </Text>
          </Card>
        ) : tiers.length === 0 ? (
          <EmptyState
            illustration={<Award size={40} />}
            title="No placement yet"
            description="Take the placement quiz to set your starting tier per skill."
          />
        ) : (
          <motion.div variants={staggerContainer} initial="hidden" animate="show">
            <Stack gap="sm">
              {tiers
                .slice()
                .sort((a, b) => b.highestUnlockedTierLevel - a.highestUnlockedTierLevel)
                .map((t) => (
                  <motion.div key={t.skillSlug} variants={rowItem}>
                    <SkillResultRow tier={t} />
                  </motion.div>
                ))}
            </Stack>
          </motion.div>
        )}
      </Section>
    </PageContainer>
  );
}

function SkillResultRow({ tier }: { tier: SkillTier }) {
  const meta = tierMeta(tier.highestUnlockedTierLevel);
  const pct = tier.proficiencyScore;
  return (
    <Card>
      <Group justify="space-between" align="center" wrap="nowrap">
        <Stack gap={4} style={{ flex: 1 }}>
          <Group gap="xs">
            <Text fw={600} size="sm">
              {skillLabel(tier.skillSlug)}
            </Text>
            <TierBadge level={tier.highestUnlockedTierLevel} size="sm" />
          </Group>
          {pct !== null ? (
            <Group gap="sm" align="center">
              <Progress
                value={pct}
                color="green"
                size="sm"
                radius="sm"
                style={{ width: 160 }}
                aria-label={`${skillLabel(tier.skillSlug)} proficiency`}
              />
              <Text size="xs" c="dimmed">
                {Math.round(pct)}% · {meta.label}
              </Text>
            </Group>
          ) : (
            <Text size="xs" c="dimmed">
              {meta.label}
            </Text>
          )}
        </Stack>
      </Group>
    </Card>
  );
}
